import React, { useRef, useLayoutEffect, useState, useEffect } from 'react'
import { AnimatePresence, motion, useMotionValue } from 'framer-motion'
import classNames from 'classnames'
import UseCasesItem from '../UseCasesItem/UseCasesItem'
import SystemStatusItem from '../SystemStatusItem/SystemStatusItem'
import CommunityItem from '../CommunityItem/CommunityItem'
import FeaturedItem from '../FeaturedItem/FeaturedItem'
import styles from './styles.module.scss'
import HeaderMenu from '../HeaderMenu/HeaderMenu'
import {
  PATH,
  solutionsDropdown,
  platformDropdown,
  industryDropdown,
  devResourcesDropdown,
  resourcesDropdown,
  demoDropdown,
} from '../../../constants/content'
import { DropdownId } from '../Header'

interface MenuContainerProps {
  selectedNavOption: DropdownId | null
  selectedNavOptionPosition: { x: number }
  darkMode?: boolean
  demoMenuEmbeddedExperiment?: boolean
  systemStatus?: {
    serviceStatus: string | null
    isLoading: boolean
    error: boolean
  }
  githubData?: {
    stargazers_count: number
  }
}

const MenuContainer = ({
  selectedNavOption,
  darkMode,
  selectedNavOptionPosition,
  demoMenuEmbeddedExperiment,
  githubData,
  systemStatus,
}: MenuContainerProps) => {
  const refs = {
    [DropdownId.Platform]: useRef<HTMLElement | null>(null),
    [DropdownId.Solutions]: useRef<HTMLElement | null>(null),
    [DropdownId.Developers]: useRef<HTMLElement | null>(null),
    [DropdownId.Resources]: useRef<HTMLElement | null>(null),
    [DropdownId.Demo]: useRef<HTMLElement | null>(null),
  }

  const containerWidth = useMotionValue<number | null>(null)
  const containerHeight = useMotionValue<number | null>(null)
  const [prevOption, setPrevOption] = useState<DropdownId | null>(null)
  const [isFirstInteraction, setIsFirstInteraction] = useState(true)
  const menuLeftPosition = useMotionValue(0)

  useEffect(() => {
    if (selectedNavOption !== null) {
      setIsFirstInteraction(false)
    } else {
      setIsFirstInteraction(true)
    }
  }, [selectedNavOption])

  useLayoutEffect(() => {
    if (!selectedNavOption) return

    const ref = refs[selectedNavOption]
    if (ref && ref.current) {
      const { clientWidth: width, clientHeight: height } = ref.current
      containerWidth.set(width)
      containerHeight.set(height)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNavOption, containerWidth, containerHeight])

  useEffect(() => {
    if (selectedNavOption !== null && selectedNavOption !== prevOption) {
      setPrevOption(selectedNavOption)
    }
  }, [selectedNavOption, prevOption])

  useLayoutEffect(() => {
    if (selectedNavOption && selectedNavOption !== DropdownId.Pricing) {
      const targetPosition =
        selectedNavOption === DropdownId.Demo && selectedNavOptionPosition?.x != null
          ? selectedNavOptionPosition.x + (containerWidth.get() ?? 0)
          : 0
      menuLeftPosition.set(targetPosition)
    }
  }, [selectedNavOption, selectedNavOptionPosition, menuLeftPosition, containerWidth])

  return (
    <AnimatePresence mode='wait'>
      {selectedNavOption !== null && selectedNavOption !== DropdownId.Pricing && (
        <motion.div
          className={styles.menuWrapper}
          style={{
            originX: 0.5,
            originY: 0,
            transformOrigin: 'top center',
            height: window.innerHeight,
            position: 'absolute',
            transform: 'translateX(-50%)',
          }}
          initial={{ opacity: 0, scale: 0.9, left: menuLeftPosition.get() }}
          animate={{ opacity: 1, scale: 1, left: menuLeftPosition.get() }}
          exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
          transition={{
            duration: 0.2,
            ease: [0.165, 0.84, 0.44, 1],
          }}
        >
          <motion.div
            className={styles.menuBody}
            style={{
              width: containerWidth,
              height: containerHeight,
              transition: isFirstInteraction ? '0' : 'width 0.2s ease, height 0.2s ease',
            }}
          >
            <div className={classNames(styles.menuContent, { [styles.darkMenuContent]: darkMode })}>
              {Object.entries(refs).map(([key, ref]) => (
                <div
                  key={key}
                  className={styles.card}
                  style={{
                    opacity: selectedNavOption === key ? 1 : 0,
                    pointerEvents: selectedNavOption === key ? 'auto' : 'none',
                  }}
                >
                  <HeaderMenu
                    darkMode={darkMode}
                    ref={ref}
                    isOpen
                    leftColumns={getLeftColumns(key, demoMenuEmbeddedExperiment)}
                    leftColumnWidth={getLeftColumnWidth(key, demoMenuEmbeddedExperiment)}
                    rightColumn={getRightColumn(key, demoMenuEmbeddedExperiment)}
                    rightColumnWidth={getRightColumnWidth(key)}
                    asidePoster={getAsidePoster(key, darkMode)}
                    bottomLeftComponent={getBottomLeftComponent(
                      key,
                      darkMode,
                      githubData,
                      demoMenuEmbeddedExperiment,
                      systemStatus
                    )}
                    bottomRightComponent={getBottomRightComponent(key, darkMode, githubData)}
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const getLeftColumns = (key: string, demoMenuEmbeddedExperiment?: boolean) => {
  switch (key) {
    case DropdownId.Platform:
      return [
        { title: 'Capabilities', list: platformDropdown.integrations },
        { title: 'Platform', list: platformDropdown.capabilities },
      ]
    case DropdownId.Solutions:
      return [
        { title: 'Protect', list: solutionsDropdown.protect },
        { title: 'Grow', list: solutionsDropdown.grow },
      ]
    case DropdownId.Developers:
      if (demoMenuEmbeddedExperiment) {
        return [{ title: 'Dev Resources', list: devResourcesDropdown, centerAligned: true }]
      }
      return [
        { list: devResourcesDropdown.slice(0, 2), centerAligned: true },
        { list: devResourcesDropdown.slice(2), centerAligned: true },
      ]
    case DropdownId.Resources:
      return [
        { list: resourcesDropdown.slice(0, 5), centerAligned: true },
        { list: resourcesDropdown.slice(5), centerAligned: true },
      ]
    case DropdownId.Demo:
      return [{ list: demoDropdown.slice(0, 2), centerAligned: true }]
    default:
      return []
  }
}

const getRightColumn = (key: string, demoMenuEmbeddedExperiment?: boolean) => {
  if (key === DropdownId.Solutions) {
    return { title: 'By Industry', list: industryDropdown }
  }
  if (key === DropdownId.Developers && demoMenuEmbeddedExperiment) {
    return { title: 'Demo', list: demoDropdown }
  }
  return undefined
}

const getRightColumnWidth = (key: string) => {
  if (key === DropdownId.Solutions) {
    return 266
  }
  if (key === DropdownId.Developers) {
    return 240
  }
  return undefined
}

const getLeftColumnWidth = (key: string, demoMenuEmbeddedExperiment?: boolean) => {
  if (key === DropdownId.Developers && !demoMenuEmbeddedExperiment) {
    return 200
  }
  if (key === DropdownId.Resources) {
    return 180
  }
  return undefined
}

const getBottomLeftComponent = (
  key: string,
  darkMode: boolean | undefined,
  githubData: { stargazers_count: number } | undefined,
  demoMenuEmbeddedExperiment?: boolean,
  systemStatus?: {
    serviceStatus: string | null
    isLoading: boolean
    error: boolean
  }
) => {
  if (key === DropdownId.Solutions) {
    return <UseCasesItem isDarkMode={darkMode} bottomLinkUrl={PATH.useCases} bottomLinkText='See all use cases' />
  }
  if (key === DropdownId.Developers) {
    return (
      <SystemStatusItem
        isDarkMode={darkMode}
        showCommunity={!demoMenuEmbeddedExperiment}
        stars={githubData?.stargazers_count}
        systemStatus={systemStatus}
      />
    )
  }
  if (key === DropdownId.Demo) {
    return (
      <DemoBottomLeftComponent
        darkMode={darkMode}
        url={demoDropdown[2].url}
        title={demoDropdown[2].title}
        Icon={demoDropdown[2].icon}
      />
    )
  }
  return undefined
}

const getAsidePoster = (key: string, darkMode: boolean | undefined) => {
  if (key === DropdownId.Resources) {
    return <FeaturedItem isDarkMode={darkMode} />
  }
  return undefined
}

const getBottomRightComponent = (
  key: string,
  darkMode: boolean | undefined,
  githubData: { stargazers_count: number } | undefined
) => {
  if (key === DropdownId.Developers) {
    return <CommunityItem stars={githubData?.stargazers_count} isDarkMode={darkMode} />
  }
  return undefined
}

const DemoBottomLeftComponent = ({
  url,
  title,
  Icon,
  darkMode,
}: {
  url: string
  title: string
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
  darkMode?: boolean
}) => {
  return (
    <>
      <div className={classNames(styles.divider, { [styles.darkDivider]: darkMode })} />
      <a href={url} rel='noreferrer'>
        <div className={classNames(styles.useCaseLink, { [styles.darkUseCaseLink]: darkMode })}>
          <div className={styles.icon}>
            <Icon />
          </div>
          {title}
        </div>
      </a>
    </>
  )
}

export default MenuContainer
